"use client";

import { RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { City } from "./landing-nav-types";

type CityPickerProps = {
    cityValue: string;
    cityLabel: string;
    cityOptions: City[];
    citiesData: City[] | undefined;
    isCityMenuOpen: boolean;
    onToggle: () => void;
    onCitySelect: (value: string, id?: string) => void;
    containerRef: RefObject<HTMLDivElement | null>;
    /** Whether to show the caret chevron (desktop has it, mobile doesn't) */
    showCaret?: boolean;
    /** Position for the dropdown */
    dropdownAlign?: "left" | "right";
};

export function CityPicker({
    cityValue,
    cityLabel,
    cityOptions,
    citiesData,
    isCityMenuOpen,
    onToggle,
    onCitySelect,
    containerRef,
    showCaret = true,
    dropdownAlign = "left",
}: CityPickerProps) {
    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={onToggle}
                aria-haspopup="listbox"
                aria-expanded={isCityMenuOpen}
                className="flex items-center gap-2 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-3 py-2 text-sm font-semibold text-[var(--hopp-text)] shadow-[0_8px_16px_rgba(3,7,18,0.35)] transition hover:border-[var(--hopp-sapphire)]"
            >
                <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4 text-[var(--hopp-sapphire)]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M12 22s7-7.75 7-13a7 7 0 10-14 0c0 5.25 7 13 7 13z" />
                    <circle cx="12" cy="9" r="2.5" />
                </svg>
                {cityLabel}
                {showCaret && (
                    <svg
                        viewBox="0 0 24 24"
                        className="h-3 w-3 text-[var(--hopp-muted)]"
                        fill="currentColor"
                    >
                        <path d="M7 10l5 5 5-5H7z" />
                    </svg>
                )}
            </button>
            <AnimatePresence>
                {isCityMenuOpen ? (
                    <motion.div
                        className={`absolute ${dropdownAlign === "left" ? "left-0" : "right-0"} top-full z-[60] mt-2 w-56 rounded-2xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] p-2 shadow-[0_20px_40px_rgba(4,8,20,0.55)]`}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                        role="listbox"
                    >
                        <button
                            type="button"
                            role="option"
                            aria-selected={cityValue === ""}
                            onClick={() => onCitySelect("", "")}
                            className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition ${
                                cityValue === ""
                                    ? "bg-[var(--hopp-panel-strong)] text-[var(--hopp-text)]"
                                    : "text-[var(--hopp-muted)] hover:bg-[var(--hopp-panel-strong)] hover:text-[var(--hopp-text)]"
                            }`}
                        >
                            All cities
                        </button>
                        <div className="my-2 h-px bg-[var(--hopp-panel-strong)]" />
                        <div className="hopp-scroll max-h-64 overflow-y-auto pr-1">
                            {cityOptions.length === 0 ? (
                                <div className="px-3 py-2 text-xs text-[var(--hopp-muted)]">
                                    {citiesData ? "No cities available" : "Loading cities..."}
                                </div>
                            ) : (
                                cityOptions.map((city) => (
                                    <button
                                        key={city.id}
                                        type="button"
                                        role="option"
                                        aria-selected={cityValue === city.name}
                                        onClick={() => onCitySelect(city.name, city.id)}
                                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition ${
                                            cityValue === city.name
                                                ? "bg-[var(--hopp-panel-strong)] text-[var(--hopp-text)]"
                                                : "text-[var(--hopp-muted)] hover:bg-[var(--hopp-panel-strong)] hover:text-[var(--hopp-text)]"
                                        }`}
                                    >
                                        {city.name}
                                    </button>
                                ))
                            )}
                        </div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </div>
    );
}
